import { z } from 'zod';
import { annualRateSchema } from './money';

/**
 * How an annual rate is determined (CPI, item growth, investment return, loan rate, …).
 * - `fixed`: a constant annual rate.
 * - `cpiRelative`: global CPI + offset. Preferred for indexed items: stays meaningful when CPI is stochastic.
 * - `distribution`: mean/volatility with optional floor/cap and AR(1) autocorrelation.
 *   Deterministic runs use the mean (clamped to floor/cap).
 */
export const fixedRateSchema = z.object({
  kind: z.literal('fixed'),
  rate: annualRateSchema,
});

export const cpiRelativeRateSchema = z.object({
  kind: z.literal('cpiRelative'),
  offset: z.number().min(-1).max(1),
});

export const distributionRateSchema = z
  .object({
    kind: z.literal('distribution'),
    mean: annualRateSchema,
    volatility: z.number().min(0).max(5),
    floor: annualRateSchema.optional(),
    cap: annualRateSchema.optional(),
    /** AR(1) coefficient in [0, 1): 0 = independent years. */
    autocorrelation: z.number().min(0).lt(1).optional(),
  })
  .refine((d) => d.floor === undefined || d.cap === undefined || d.floor <= d.cap, {
    message: 'Floor must not exceed cap',
    path: ['cap'],
  });

export const rateSpecSchema = z.discriminatedUnion('kind', [
  fixedRateSchema,
  cpiRelativeRateSchema,
  distributionRateSchema,
]);

/** Rate kinds allowed where "relative to CPI" is meaningless (the global CPI itself). */
export const absoluteRateSpecSchema = z.discriminatedUnion('kind', [
  fixedRateSchema,
  distributionRateSchema,
]);

/** Placeholder segment value: "use the next level of the inflation chain here". */
export const inheritSchema = z.object({ kind: z.literal('inherit') });

export const rateOrInheritSchema = z.discriminatedUnion('kind', [
  fixedRateSchema,
  cpiRelativeRateSchema,
  distributionRateSchema,
  inheritSchema,
]);

export type RateSpec = z.infer<typeof rateSpecSchema>;
export type AbsoluteRateSpec = z.infer<typeof absoluteRateSpecSchema>;
export type Inherit = z.infer<typeof inheritSchema>;
export type RateOrInherit = z.infer<typeof rateOrInheritSchema>;

export const fixedRate = (rate: number) => ({ kind: 'fixed', rate }) as const satisfies RateSpec;
export const cpiPlus = (offset: number) =>
  ({ kind: 'cpiRelative', offset }) as const satisfies RateSpec;
export const INHERIT: Inherit = { kind: 'inherit' };

/** The value a deterministic run uses for a distribution: its mean, clamped to floor/cap. */
export function distributionCentre(d: {
  mean: number;
  floor?: number | undefined;
  cap?: number | undefined;
}): number {
  let value = d.mean;
  if (d.floor !== undefined && value < d.floor) value = d.floor;
  if (d.cap !== undefined && value > d.cap) value = d.cap;
  return value;
}
